import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, X, Pencil, Move, Zap } from "lucide-react";
import { API, useAuth } from "../App";
import { ChapterVoice } from "./ChapterVoice";
import { useRadio } from "./RadioContext";
import { describePoint, mapFor, resolveAnchor } from "./pinAnchor";
import { ViewItButton, readPendingPlacement, setPendingPlacement } from "./ViewIt";

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }, withCredentials: true });
// "Play when seen": one autoplay per page view, never over music, never interrupting another clip, only after the first tap.
const autoPlayedHere = new Set();
const AUTOPLAY_NEXT = { off: "once", once: "always", always: "off" };
const AUTOPLAY_LABEL = { off: "Auto-play off", once: "Auto-plays when seen · once per visit", always: "Auto-plays when seen · every time" };

const isField = (el) => !!el?.closest?.("input, textarea, select, button, [role=button], [contenteditable=true], [data-hear-pins-layer], [data-testid=front-edit-panel]");

// One pin: a dot at the exact spot + a Hear-it card (clips via ChapterVoice, attach = pin:<id>).
const Pin = ({ pin, isAdmin, pathname, onChange, onRemove, layer }) => {
  const [pos, setPos] = useState(null);
  const drag = useRef(null);
  const [ghost, setGhost] = useState(null);
  const [clips, setClips] = useState([]);
  const [cued, setCued] = useState(false);
  const r = useRadio();
  const cardRef = useRef(null);
  const mode = pin.autoplay || "off";

  useEffect(() => {
    if (mode === "off" || !clips.length || !cardRef.current || !pos) return;
    const onceKey = `wall_pin_auto_${pin.pin_id}`;
    if (mode === "once" && sessionStorage.getItem(onceKey)) return;
    const io = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      if (autoPlayedHere.has(pin.pin_id)) return;
      if (window.__wallMusicPlaying || window.__wallVoicePlaying || !window.__wallInteracted) { setCued(true); return; }  // just the green dot
      autoPlayedHere.add(pin.pin_id);
      if (mode === "once") sessionStorage.setItem(onceKey, "1");
      r.load(clips[0], 0);
      setTimeout(() => r.setMinimized(true), 50);
      setCued(false);
      io.disconnect();
    }, { threshold: 0.6 });
    io.observe(cardRef.current);
    return () => io.disconnect();
  }, [mode, clips, pos, pin.pin_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const [geoMap, setGeoMap] = useState(null);
  const place = useCallback(() => {
    const el = resolveAnchor(pin);
    if (pin.lat != null && pin.lng != null) {  // map pin: follow the map, not the page box
      const m = mapFor(el) || mapFor(document.querySelector(".leaflet-container"));
      if (!m) return setPos(null);
      setGeoMap((cur) => (cur === m.map ? cur : m.map));
      let pt;
      try { pt = m.map.latLngToContainerPoint([pin.lat, pin.lng]); } catch { return setPos(null); }
      const r = m.c.getBoundingClientRect();
      if (pt.x < 0 || pt.y < 0 || pt.x > r.width || pt.y > r.height) return setPos(null);
      return setPos({ left: r.left + window.scrollX + pt.x, top: r.top + window.scrollY + pt.y });
    }
    if (!el) return setPos(null);
    const r = el.getBoundingClientRect();
    setPos({ left: r.left + window.scrollX + pin.x * r.width, top: r.top + window.scrollY + pin.y * r.height });
  }, [pin]);
  useEffect(place, [place, pin.updated_at]);
  useEffect(() => { window.addEventListener("hearpins:layout", place); return () => window.removeEventListener("hearpins:layout", place); }, [place]);
  useEffect(() => {
    if (!geoMap) return;
    const ev = "move zoom viewreset resize";
    geoMap.on(ev, place);
    return () => geoMap.off(ev, place);
  }, [geoMap, place]);

  const onDown = (e) => { e.preventDefault(); drag.current = true; e.currentTarget.setPointerCapture?.(e.pointerId); };
  const onMove = (e) => { if (drag.current) setGhost({ left: e.clientX + window.scrollX, top: e.clientY + window.scrollY }); };
  const onUp = async (e) => {
    if (!drag.current) return;
    drag.current = false; setGhost(null);
    const d = describePoint(e.clientX, e.clientY, layer);
    if (!d) return;
    if (d.lat == null && pin.lat != null) d.clear_geo = true;
    try { onChange((await axios.patch(`${API}/admin/hear-pins/${pin.pin_id}`, d, auth())).data); } catch { toast.error("Couldn't move the pin"); }
  };
  const rename = async () => {
    const t = window.prompt("Pin title — shown above the Hear-it button (leave blank for the default)", pin.label || "");
    if (t === null) return;
    try { onChange((await axios.patch(`${API}/admin/hear-pins/${pin.pin_id}`, { label: t }, auth())).data); } catch { toast.error("Couldn't rename"); }
  };
  const cycleAutoplay = async () => {
    const next = AUTOPLAY_NEXT[mode];
    try { onChange((await axios.patch(`${API}/admin/hear-pins/${pin.pin_id}`, { autoplay: next }, auth())).data); toast.success(AUTOPLAY_LABEL[next]); } catch { toast.error("Couldn't change auto-play"); }
  };
  // Two-tap remove (no native confirm — iOS swallows those mid-recording): first tap arms, second within 4 s deletes.
  const [armed, setArmed] = useState(false);
  useEffect(() => { if (!armed) return; const t = setTimeout(() => setArmed(false), 4000); return () => clearTimeout(t); }, [armed]);
  const remove = async () => {
    if (!armed) { setArmed(true); return; }
    try { await axios.delete(`${API}/admin/hear-pins/${pin.pin_id}`, auth()); onRemove(pin.pin_id); toast.success(pin.kind === "view" ? "View-it removed — the video is still in the Video Desk" : "Pin removed — its clips stay in the Radio panel"); }
    catch (e) { if (e.response?.status === 404) onRemove(pin.pin_id); else toast.error("Couldn't remove"); }
  };

  if (!pos) return null;
  const at = ghost || pos;
  const isView = pin.kind === "view";
  return (
    <div className="absolute" style={{ left: at.left, top: at.top }} data-hear-pin data-kind={isView ? "view" : "hear"} data-testid={`hear-pin-${pin.pin_id}`}>
      <span className={`absolute -left-1.5 -top-1.5 h-3 w-3 rounded-full ring-2 ring-[#120A24] ${isView ? "bg-[#FF3B3B] animate-[pulse_2.6s_ease-in-out_infinite]" : cued ? "bg-emerald-400 animate-pulse" : "bg-[#54E3FF]"} ${ghost ? "opacity-60 scale-150" : ""}`} aria-hidden title={cued ? "Tap Hear it to listen" : undefined} data-testid={`hear-pin-dot-${pin.pin_id}`} data-cued={cued ? "1" : "0"} />
      <div ref={cardRef} className={`absolute left-2 top-2 w-max max-w-[min(18rem,calc(100vw-2rem))] rounded-2xl bg-[#FBFAF7]/95 backdrop-blur px-3 pb-2.5 pt-1.5 shadow-xl ring-1 pointer-events-auto ${isView ? "ring-[#FF3B3B]/50" : "ring-[#54E3FF]/50"}`} style={{ visibility: ghost ? "hidden" : "visible" }}>
        {isAdmin && (
          <div className="flex items-center justify-end gap-0.5 -mr-1 text-[#5C5C5C]" data-testid={`hear-pin-tools-${pin.pin_id}`}>
            <button type="button" onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={() => { drag.current = false; setGhost(null); }} className="p-1 cursor-grab active:cursor-grabbing touch-none hover:text-[#1A1A1A]" aria-label="Drag to move pin" title="Drag to move" data-testid={`hear-pin-move-${pin.pin_id}`}><Move className="h-3.5 w-3.5" /></button>
            <button type="button" onClick={rename} className="p-1 hover:text-[#1A1A1A]" aria-label="Rename pin" title="Title above the button" data-testid={`hear-pin-rename-${pin.pin_id}`}><Pencil className="h-3.5 w-3.5" /></button>
            {!isView && <button type="button" onClick={cycleAutoplay} className={`p-1 ${mode === "off" ? "hover:text-[#1A1A1A]" : "text-emerald-600"}`} aria-label="Auto-play when seen" title={`${AUTOPLAY_LABEL[mode]} — tap to change. Never plays over music; before the visitor's first tap it just shows a green dot.`} data-testid={`hear-pin-autoplay-${pin.pin_id}`} data-mode={mode}><Zap className="h-3.5 w-3.5" /></button>}
            <button type="button" onClick={remove} className={`p-1 rounded-full inline-flex items-center gap-1 ${armed ? "bg-red-500 text-white px-2 text-[10px] font-black" : "text-red-500/80 hover:text-red-500"}`} aria-label={armed ? "Tap again to remove pin" : "Remove pin"} data-testid={`hear-pin-remove-${pin.pin_id}`} data-armed={armed ? "1" : "0"}><X className="h-3.5 w-3.5" />{armed && "Remove?"}</button>
          </div>
        )}
        {isView ? <ViewItButton pin={pin} /> : (
          <>
            {mode !== "off" && isAdmin && <p className="m-0 text-[10px] text-emerald-700 font-semibold">{AUTOPLAY_LABEL[mode]}</p>}
            <ChapterVoice attach={{ kind: "pin", slug: pin.pin_id, name: pin.label || "Hear-it pin" }} heading={pin.label || undefined} back={pathname} className="!mt-0" onClips={setClips} />
          </>
        )}
      </div>
    </div>
  );
};

// Admin gestures (Pin Mode only): four quick taps on one spot drop a pin; the follow-up click is swallowed briefly. (No long-press — it caused accidental pins.)
function usePlacementGestures(enabled, drop) {
  useEffect(() => {
    if (!enabled) return;
    const taps = [];
    let swallowUntil = 0;
    const near = (a, b) => Math.hypot(a.x - b.x, a.y - b.y) < 40;
    const down = (e) => {
      if (e.button > 0 || isField(e.target) || e.target.closest?.("[data-hear-pin]")) return;  // taps on an existing pin's card never count
      const p = { x: e.clientX, y: e.clientY, t: Date.now() };
      const recent = taps.filter((q) => p.t - q.t < 900 && near(p, q));
      if (recent.length >= 3) { swallowUntil = Date.now() + 400; taps.length = 0; drop(p.x, p.y); return; }
      taps.splice(0, taps.length, ...recent, p);
    };
    const click = (e) => { if (Date.now() < swallowUntil) { e.preventDefault(); e.stopPropagation(); swallowUntil = 0; } };
    document.addEventListener("pointerdown", down, true);
    document.addEventListener("click", click, true);
    return () => {
      document.removeEventListener("pointerdown", down, true);
      document.removeEventListener("click", click, true);
    };
  }, [enabled, drop]);
}

// Mounted once in the app. Loads this page's pins, keeps them glued to their anchors, and gives admins Pin Mode (+ View-it placement).
export const HearPinsLayer = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [pins, setPins] = useState([]);
  const [mode, setMode] = useState(false);
  const [pending, setPending] = useState(readPendingPlacement);  // a video waiting to be placed (from the Video Desk)
  const [confirm, setConfirm] = useState(null);  // {x, y, d} — "Add it here?" card for a View-it drop
  const layer = useRef(null);
  const isAdmin = user?.role === "admin" || user?.role === "magician";
  const onAdminPage = pathname.startsWith("/admin") || pathname.startsWith("/auth");

  useEffect(() => { const h = () => setPending(readPendingPlacement()); window.addEventListener("wall:place-video", h); return () => window.removeEventListener("wall:place-video", h); }, []);
  useEffect(() => { setMode(false); setConfirm(null); axios.get(`${API}/hear-pins`, { params: { page: pathname } }).then((r) => setPins(r.data || [])).catch(() => setPins([])); }, [pathname]);

  // Re-measure anchors whenever the page's layout changes (content loads, images settle, resize).
  useEffect(() => {
    let t;
    const bump = () => { clearTimeout(t); t = setTimeout(() => window.dispatchEvent(new Event("hearpins:layout")), 120); };
    const mo = new MutationObserver((recs) => { if (recs.some((r) => !layer.current?.contains(r.target))) bump(); });
    mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["class", "style", "open"] });
    const ro = new ResizeObserver(bump); ro.observe(document.body);
    window.addEventListener("resize", bump); window.addEventListener("load", bump);
    const iv = setInterval(bump, 2500);
    return () => { mo.disconnect(); ro.disconnect(); window.removeEventListener("resize", bump); window.removeEventListener("load", bump); clearInterval(iv); clearTimeout(t); };
  }, []);

  useEffect(() => { if (!mode && !confirm) return; const k = (e) => { if (e.key === "Escape") { setMode(false); setConfirm(null); } }; window.addEventListener("keydown", k); return () => window.removeEventListener("keydown", k); }, [mode, confirm]);

  const drop = useCallback(async (clientX, clientY) => {
    const d = describePoint(clientX, clientY, layer.current);
    if (!d) return;
    if (pending) { setConfirm({ x: clientX, y: clientY, d }); setMode(false); return; }  // View-it: ask first
    try {
      const res = await axios.post(`${API}/admin/hear-pins`, { page: pathname, ...d, label: "" }, auth());
      setPins((p) => [...p, res.data]); setMode(false);
      toast.success("Pin dropped — tap “Record a clip” on it to give it a voice");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't place the pin"); }
  }, [pathname, pending]);
  usePlacementGestures(isAdmin && !mode && !onAdminPage && !pending, drop);

  const confirmView = async () => {
    if (!confirm || !pending) return;
    try {
      const res = await axios.post(`${API}/admin/hear-pins`, { page: pathname, ...confirm.d, label: "", kind: "view", media_id: pending.media_id }, auth());
      const full = { ...res.data, media: pending.media || { title: pending.title } };
      setPins((p) => [...p, full]); setConfirm(null); setPendingPlacement(null);
      toast.success("Et voilà — View it is on the page", { description: "Drag the dot to nudge it; the pencil adds a title above the button." });
      axios.get(`${API}/hear-pins`, { params: { page: pathname } }).then((r) => setPins(r.data || [])).catch(() => {});
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't place View it"); }
  };

  const onModeClick = (e) => { e.preventDefault(); e.stopPropagation(); const ov = e.currentTarget; ov.style.pointerEvents = "none"; drop(e.clientX, e.clientY); ov.style.pointerEvents = ""; };
  const shown = isAdmin ? pins : pins.filter((p) => p.clip_count > 0);
  const update = (np) => setPins((ps) => ps.map((p) => (p.pin_id === np.pin_id ? np : p)));
  const remove = (id) => setPins((ps) => ps.filter((p) => p.pin_id !== id));

  return createPortal(
    <>
      <div ref={layer} className="absolute left-0 top-0 z-[45] pointer-events-none" style={{ width: 0, height: 0 }} data-hear-pins-layer data-testid="hear-pins-layer">
        {shown.map((p) => <Pin key={p.pin_id} pin={p} isAdmin={isAdmin} pathname={pathname} onChange={update} onRemove={remove} layer={layer.current} />)}
      </div>
      {isAdmin && !onAdminPage && !pending && (
        <button type="button" onClick={() => setMode((m) => !m)} className={`fixed right-4 bottom-28 sm:bottom-16 z-[60] inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold shadow-lg transition-colors ${mode ? "bg-[#54E3FF] text-[#120A24]" : "bg-[#1A1A1A] text-white hover:bg-[#2E5C55]"}`} title="Pin Mode — tap once to place a Hear-it button. (Outside Pin Mode: 4 quick taps on one spot also drops one.)" data-testid="hear-pin-mode-chip">
          <MapPin className="h-3.5 w-3.5" /> {mode ? "Pin Mode · tap a spot" : "Pin Mode"}
        </button>
      )}
      {isAdmin && pending && !onAdminPage && (
        <div className="fixed right-4 bottom-28 sm:bottom-16 z-[60] flex items-center gap-1.5 rounded-full bg-[#1A1A1A] text-white pl-3 pr-1.5 py-1.5 text-xs font-semibold shadow-lg ring-2 ring-[#FF3B3B]" data-testid="view-it-place-bar">
          <span className="h-2.5 w-2.5 rounded-full bg-[#FF3B3B] animate-[pulse_2.6s_ease-in-out_infinite]" />
          <span className="max-w-[9rem] truncate">Placing «{pending.title || "video"}»</span>
          <button type="button" onClick={() => setMode((m) => !m)} className={`rounded-full px-3 py-1.5 font-black ${mode ? "bg-white text-[#FF3B3B]" : "bg-[#FF3B3B] text-white"}`} data-testid="view-it-drop-here">{mode ? "tap a spot…" : "Drop here"}</button>
          <button type="button" onClick={() => { setPendingPlacement(null); setMode(false); setConfirm(null); }} className="p-1.5 text-white/60 hover:text-white" aria-label="Cancel placing" data-testid="view-it-place-cancel"><X className="h-3.5 w-3.5" /></button>
        </div>
      )}
      {mode && (
        <div onClick={onModeClick} className={`fixed inset-0 z-[58] cursor-crosshair ${pending ? "bg-[#FF3B3B]/5" : "bg-[#54E3FF]/5"}`} data-testid="hear-pin-mode-overlay">
          <div className={`pointer-events-none fixed left-1/2 top-20 -translate-x-1/2 rounded-full bg-[#120A24] px-4 py-2 text-xs font-black shadow-xl ring-1 ${pending ? "text-[#FF3B3B] ring-[#FF3B3B]/60" : "text-[#54E3FF] ring-[#54E3FF]/60"}`} data-testid="hear-pin-mode-banner">{pending ? "Tap where the View-it button should go · Esc to cancel" : "Tap where the Hear-it button should go · Esc to cancel"}</div>
        </div>
      )}
      {confirm && pending && (
        <div className="fixed z-[70] rounded-2xl bg-[#FBFAF7] text-[#1A1A1A] shadow-2xl ring-2 ring-[#FF3B3B] p-3 w-64" style={{ left: Math.min(confirm.x, window.innerWidth - 272), top: Math.min(confirm.y + 12, window.innerHeight - 140) }} data-testid="view-it-confirm">
          <p className="m-0 text-xs font-black inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-[#FF3B3B] animate-[pulse_2.6s_ease-in-out_infinite]" /> Add «{pending.title || "video"}» here?</p>
          <p className="m-0 mt-0.5 text-[11px] text-[#5C5C5C]">A View-it button appears at this spot.</p>
          <div className="mt-2 flex gap-1.5">
            <button type="button" onClick={confirmView} className="flex-1 rounded-full bg-[#FF3B3B] text-white px-3 py-1.5 text-xs font-black" data-testid="view-it-confirm-yes">Yes, add View it</button>
            <button type="button" onClick={() => setConfirm(null)} className="rounded-full bg-[#F3F1EC] px-3 py-1.5 text-xs font-bold" data-testid="view-it-confirm-no">Not here</button>
          </div>
        </div>
      )}
    </>,
    document.body
  );
};
